import React from 'react';
import styled from 'styled-components';
import RCore from 'RCore';
import _ from 'lodash';
import { SimpleCard } from 'SportIcons';
import MatchScore from './MatchScore';
import PropTypes from 'prop-types';

import { PlayerLogo3DJerseyBack as PlayerLogo } from '../../../components/sm/views/playerLogo/SMPlayerLogo';

const dict = RCore.i18Register(require('../../../i18/i18.IncidentWidget.js'), {});
const i18 = RCore.getTranslation.bind({ dict });

const Text = styled.span`
    font-size: ${props => props.size}px;
    line-height: 1.1;
    vertical-align: middle;
    display: inline-block;
    white-space: nowrap;
`;
const Space = styled.div`
    height: ${props => props.size}px;
`;
const Flex = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;
const CardIconWrapper = styled.div`
    transform: rotate(15deg) translateY(-4px);
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.5);
    border-radius: 3px;
    overflow: hidden;
    display: inline-block;
`;
const SubstitutionType = styled.div`
    font-size: 36px;
    text-transform: uppercase;
    margin-right: 20px;
    color: ${props => (props.in ? props.theme.positiveColor : props.theme.substitutionOutColor)};
    > span {
        display: inline-block;
        vertical-align: middle;
    }
    &:before {
        content: '';
        display: inline-block;
        margin-right: 20px;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 20px 0 20px 30px;
        border-color: transparent transparent transparent
            ${props => (props.in ? props.theme.positiveColor : props.theme.substitutionOutColor)};
        vertical-align: middle;
        ${props => (props.in ? 'transform: rotate(180deg);' : '')};
    }
`;

const EllipsisOverflow = styled.span`
    display: inline-block;
    text-overflow: ellipsis;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
`;

const InfoWrapper = styled.div`
    width: calc(800px - 320px - 30px - ${props => props.theme.contentPadding * 2}px);
    text-align: center;

    strong {
        display: flex;
        justify-content: center;
    }
`;

const MainContent = styled.div`
    text-align: left;
    display: flex;
    justify-content: center;
`;

const ContentWrapper = styled.div`
    text-align: center;
    margin: auto;
`;

const ShirtWrapper = styled.div`
    position: relative;
    width: ${props => props.width || '320'}px;
    img {
        height: auto;
    }
`;

const MatchScoreWrapper = styled.div`
    .wdp-team-logo {
        height: 60px !important;
    }
    .team-name {
        width: calc(50% - 80px) !important;
    }
    .score-box {
        font-size: 50px !important;
        line-height: 1;
        font-weight: 400 !important;
    }
`;

const getPlayerName = ({ shortFirstName, lastName }) => {
    return `${shortFirstName} ${lastName}`;
};

const getPlayerShirt = ({ event, player, incident, width }) => {
    let isHome = incident.home === 1;
    if (incident.typeId === 10) isHome = !isHome; // swap teams for own goal

    return (
        <ShirtWrapper width={width}>
            <PlayerLogo
                display="3d_jersey_back"
                number={_.get(player, 'shirtNumber')}
                playerId={_.get(player, 'id')}
                width={width || 320}
                seasonId={event.tournamentId}
                teamId={isHome ? event.homeId : event.awayId}
            />
        </ShirtWrapper>
    );
};

const Score = ({ incidentId, event, matchScore }) => {
    return (
        <MatchScoreWrapper className="__c__c5">
            <MatchScore
                {...matchScore}
                event={event}
                view="simpleLogoScore"
                styles="font-size: 50px;"
                incidentId={incidentId}
            />
        </MatchScoreWrapper>
    );
};

const Wrapper = ({ children, matchScore, event, incidentId, incidentMinute, incidentColor }) => {
    return (
        <ContentWrapper>
            <MainContent>{children}</MainContent>
        </ContentWrapper>
    );
};

const Assist = (props, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');
    return (
        <Flex>
            <div style={{ marginRight: '10px' }}>{getPlayerShirt(props)}</div>
            <InfoWrapper>
                <strong>
                    <Text size={110} bold>
                        {i18('title.assist', lang).toUpperCase()}
                    </Text>
                </strong>
                <Space size={10} />
                <IncidentInfo {...props} />
            </InfoWrapper>
        </Flex>
    );
};

const IncidentInfo = (props, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');
    const playerName = getPlayerName(props.playerName);

    const fontSize = playerName.length < 15 ? 50 : 30;

    return (
        <div>
            <EllipsisOverflow>
                <Text
                    style={{
                        maxWidth: '100%',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                    className="__c__c1"
                    size={fontSize}
                >
                    {playerName}
                </Text>
            </EllipsisOverflow>
            <Space size={20} />
            <Score {...props} />
            <Text className="__c__c2" size={30} style={{ marginLeft: 10 }}>
                {props.incidentMinute} {i18('min', lang)}.
            </Text>
        </div>
    );
};

const Goal = (props, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');
    return (
        <Flex>
            <div style={{ marginRight: '10px' }}>{getPlayerShirt(props)}</div>
            <InfoWrapper>
                <strong>
                    <Text size={130} bold className="__c__c3">
                        {i18('title.goal', lang).toUpperCase()}
                    </Text>
                </strong>
                <Space size={10} />
                <IncidentInfo {...props} />
            </InfoWrapper>
        </Flex>
    );
};

const OwnGoal = (props, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');

    return (
        <Flex>
            <div style={{ marginRight: '10px' }}>{getPlayerShirt(props)}</div>
            <InfoWrapper>
                <strong>
                    <Text size={75} bold style={{ color: props.incidentMinuteColor }} className="__c__c4">
                        {i18('title.owngoal', lang).toUpperCase()}
                    </Text>
                </strong>
                <Space size={10} />
                <IncidentInfo {...props} />
            </InfoWrapper>
        </Flex>
    );
};
const Card = ({ cardColor, cardName, playerName, ...props }) => {
    return (
        <div>
            <Flex>
                <div style={{ marginRight: '10px' }}>{getPlayerShirt(props)}</div>
                <InfoWrapper>
                    <CardIconWrapper>
                        <SimpleCard width={76} height={100} color={cardColor} />
                    </CardIconWrapper>
                    <Space size={25} />
                    <IncidentInfo {...props} playerName={playerName} />
                </InfoWrapper>
            </Flex>
        </div>
    );
};
const Substitution = (props, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');
    return (
        <Flex>
            <div style={{ position: 'relative', marginTop: -200, marginRight: 100 }}>
                <div
                    style={{
                        width: 150,
                    }}
                >
                    {getPlayerShirt({ ...props, player: props.player2, width: 150 })}
                </div>
                <div
                    style={{
                        width: 200,
                        position: 'absolute',
                        bottom: -200,
                        left: 30,
                        zIndex: 2,
                    }}
                >
                    {getPlayerShirt({ ...props, width: 200 })}
                </div>
            </div>
            <div>
                <div
                    style={{
                        whiteSpace: 'nowrap',
                        marginTop: 75,
                        marginLeft: -40,
                        overflow: 'hidden',
                    }}
                >
                    <SubstitutionType out style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                        <span>{i18('out', lang)}</span>
                    </SubstitutionType>
                    <Text
                        size={40}
                        style={{
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            width: '70%',
                            textAlign: 'left',
                        }}
                    >
                        <EllipsisOverflow style={{ width: '100%' }}>
                            <Text
                                className="__c__c14"
                                style={{
                                    maxWidth: '100%',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                }}
                                size={50}
                            >
                                {getPlayerName(props.player2Name)}
                            </Text>
                        </EllipsisOverflow>
                    </Text>
                </div>
                <div
                    style={{
                        opacity: 0.85,
                        whiteSpace: 'nowrap',
                        marginTop: 20,
                        overflow: 'hidden',
                    }}
                >
                    <SubstitutionType in style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                        <span>{i18('in', lang)}</span>
                    </SubstitutionType>
                    <Text
                        size={50}
                        style={{
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            width: '100%',
                            textAlign: 'left',
                        }}
                    >
                        <div style={{ width: '100%' }}>
                            <Text
                                className="__c__c12"
                                style={{
                                    maxWidth: '80%',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                }}
                                size={50}
                            >
                                {getPlayerName(props.playerName)}
                            </Text>
                        </div>
                    </Text>
                    <Space size={40} />
                    <Score {...props} />
                    <Text className="__c__c13" size={30} style={{ textAlign: 'center', width: '100%' }}>
                        {props.incidentMinute} {i18('min', lang)}.
                    </Text>
                </div>
            </div>
        </Flex>
    );
};

Goal.contextTypes = {
    info: PropTypes.object,
};

Wrapper.contextTypes = {
    info: PropTypes.object,
};
Assist.contextTypes = {
    info: PropTypes.object,
};
OwnGoal.contextTypes = {
    info: PropTypes.object,
};
Card.contextTypes = {
    info: PropTypes.object,
};
Substitution.contextTypes = {
    info: PropTypes.object,
};

export default {
    Wrapper,
    Goal,
    Assist,
    OwnGoal,
    Card,
    Substitution,
    disableBranding: true,
    contentProps: { style: { height: 550 } },
};

export { Wrapper, Goal, Assist, OwnGoal, Card, Substitution };



/** WEBPACK FOOTER **
 ** ../src/widgets/components/sm/views/IncidentWithoutPhotos.js
 **/